
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import EmailVerification from './EmailVerification';
import PendingApproval from './PendingApproval';
import PinSetup from './PinSetup';
import PermissionDenied from './PermissionDenied';
import { Loader2 } from 'lucide-react';
import { DeviceType, PermissionMatrixAction, PermissionMatrixModule, Role, TenantPermissionMatrixRule } from '../types';
import { useDevice } from '../context/DeviceContext';
import { useTenant } from '../context/TenantContext';
import { TenantModuleFlags } from '../config/tenant';
import { db } from '../services/firebase';
import { getDoc } from 'firebase/firestore';
import { tenantPermissionsMatrixDocRef } from '../services/firestorePaths';
import { evaluateRouteAccess, getEffectiveDeviceType } from '../utils/routeAccess';
import { evaluateQuickStartModuleGuards } from '../utils/quickStartGuards';
import { trackPermissionDenied } from '../services/analyticsService';
import { useDeviceUnlock } from '../context/DeviceUnlockContext';
import TabletUnlockGate from './TabletUnlockGate';
import CrewSignOnComponent from './CrewSignOnComponent';
import { useOptionalCrewSession } from '../context/CrewContext';
import { isAspisOperationsHost, isPlatformOperator } from '../utils/platformOps';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  allowedRoles?: Role[];
  allowedDeviceTypes?: DeviceType[];
  requiredModules?: Array<keyof TenantModuleFlags>;
  requiredPermission?: {
    module: PermissionMatrixModule;
    action: PermissionMatrixAction;
  };
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles, allowedDeviceTypes, requiredModules, requiredPermission }) => {
  const { user, isLoading, firebaseUser } = useAuth();
  const { deviceType, isRegistered, isChecking } = useDevice();
  const { isUnlockRequired, isLocked } = useDeviceUnlock();
  const { crewSignedOn, loadingShift } = useOptionalCrewSession();
  const { tenant } = useTenant();
  const location = useLocation();
  const [permissionResolved, setPermissionResolved] = useState(false);
  const [permissionAllowed, setPermissionAllowed] = useState(true);
  const [permissionError, setPermissionError] = useState(false);
  const lastDeniedKeyRef = useRef<string>('');

  const emitDenied = (reason: string, details?: string) => {
    const deniedKey = `${location.pathname}|${reason}|${user?.role || 'anonymous'}`;
    if (lastDeniedKeyRef.current === deniedKey) return;
    lastDeniedKeyRef.current = deniedKey;

    trackPermissionDenied(location.pathname, reason);

    if (details) {
      console.warn(`[GuardDenied] ${reason}: ${details}`);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const resolvePermission = async () => {
      if (!requiredPermission || !user?.tenantId) {
        if (!cancelled) {
          setPermissionAllowed(true);
          setPermissionError(false);
          setPermissionResolved(true);
        }
        return;
      }

      if (user.role === Role.Admin) {
        if (!cancelled) {
          setPermissionAllowed(true);
          setPermissionError(false);
          setPermissionResolved(true);
        }
        return;
      }

      try {
        const matrixSnap = await getDoc(tenantPermissionsMatrixDocRef(db, user.tenantId));
        const matrixRules = matrixSnap.exists() ? (matrixSnap.data().rules || []) as TenantPermissionMatrixRule[] : [];
        const matched = matrixRules.find(
          (rule) => rule.module === requiredPermission.module && rule.action === requiredPermission.action
        );

        // Check roleGroupId grant first, then fall back to base role grant.
        // Fail closed when a route permission rule is missing.
        let resolvedAllow = false;
        if (matched) {
          if (user.roleGroupId && matched.grants?.[user.roleGroupId] !== undefined) {
            resolvedAllow = !!matched.grants[user.roleGroupId];
          } else {
            resolvedAllow = !!matched.grants?.[user.role];
          }
        }

        if (!cancelled) {
          setPermissionAllowed(resolvedAllow);
          setPermissionError(false);
          setPermissionResolved(true);
        }
      } catch {
        if (!cancelled) {
          // Fail closed if permissions cannot be verified.
          setPermissionAllowed(false);
          setPermissionError(true);
          setPermissionResolved(true);
        }
      }
    };

    setPermissionResolved(false);
    resolvePermission();

    return () => {
      cancelled = true;
    };
  }, [requiredPermission, user?.tenantId, user?.role, user?.roleGroupId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Safety Check
  if (firebaseUser && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Device registration check for route policy enforcement.
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // 1. Check if logged in
  if (!user || !firebaseUser) {
    emitDenied('unauthenticated', 'Missing auth context for protected route.');
    return <Navigate to="/login" replace />;
  }

  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const platformOperator = isPlatformOperator(firebaseUser.email);
  const isOpsHost = isAspisOperationsHost(currentHost);
  const requestingPlatformOpsRoute = location.pathname.startsWith('/platform-ops');

  if (platformOperator && !isOpsHost) {
    emitDenied('platform_ops_host_restriction', `Platform operator account blocked on tenant host ${currentHost}.`);
    return (
      <PermissionDenied
        title="Tenant Workspace Access Restricted"
        description="Aspis operations accounts are not permitted inside company tenant portals."
        details="Sign in on aspis.software to access the global Platform Ops console."
      />
    );
  }

  if (requestingPlatformOpsRoute && !isOpsHost) {
    emitDenied('platform_ops_route_host_restriction', `Platform Ops route blocked on non-ops host ${currentHost}.`);
    return (
      <PermissionDenied
        title="Platform Ops Host Required"
        description="Platform Ops is only available from the global aspis.software host."
        details="Company subdomains cannot open Platform Ops routes."
      />
    );
  }

  // 2. Check Email Verification
  if (!firebaseUser.emailVerified) {
      emitDenied('email_unverified', 'Account email is not verified.');
      return <EmailVerification />;
  }

  // 3. Check Account Status
  if (user.status === 'Pending') {
      emitDenied('account_pending', 'Account is pending approval.');
      return <PendingApproval />;
  }
  
  if (user.status === 'Suspended' || user.status === 'Rejected') {
      emitDenied('account_restricted', `Account status is ${user.status}.`);
      return (
        <PermissionDenied
          title="Account Access Restricted"
          description="Your account is currently not active for operational access."
          details={`Current account status: ${user.status}. Please contact your manager or support to restore access.`}
        />
      );
  }

  // 4. Check PIN Setup (New Requirement)
  // If user is Active but has no PIN, force setup
  if (user.status === 'Active' && !user.pin) {
      emitDenied('pin_missing', 'Active account has no PIN set.');
      return <PinSetup />;
  }

  if (isUnlockRequired && isLocked) {
    emitDenied('device_locked', 'Ambulance tablet unlock required after idle timeout.');
    return <TabletUnlockGate />;
  }

  const requiresCrewSignOn = isUnlockRequired && Array.isArray(requiredModules) && requiredModules.includes('clinicalOps');
  if (requiresCrewSignOn && loadingShift) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (requiresCrewSignOn && !crewSignedOn) {
    emitDenied('crew_signon_required', 'Crew sign-on required before clinical operations routes.');
    return <CrewSignOnComponent tenantId={tenant.tenantId} />;
  }

  // 5, 6, 7. Check role + device + tenant module access.
  const accessResult = evaluateRouteAccess({
    role: user.role,
    deviceType,
    isRegistered,
    modules: tenant.modules,
    allowedRoles,
    allowedDeviceTypes,
    requiredModules,
  });
  if (!accessResult.allowed) {
    if (accessResult.reason === 'role') {
      emitDenied('role', `Role ${user.role} blocked for route.`);
      const isManager = user.role === Role.Manager || user.role === Role.Admin;
      return (
        <PermissionDenied
          title="Role Access Restricted"
          description="Your current role does not have access to this view."
          details={`Role: ${user.role}. Required: ${(allowedRoles || []).join(', ')}.`}
          reason="role"
          recoveryCta={
            isManager
              ? { label: 'Review permissions in Quick Start', to: '/quick-start' }
              : { label: 'Contact your manager to request access', to: '/wellbeing' }
          }
        />
      );
    }

    if (accessResult.reason === 'device') {
      const effectiveDeviceType = getEffectiveDeviceType(deviceType, isRegistered);
      emitDenied('device', `Device ${effectiveDeviceType} blocked for route.`);
      return (
        <PermissionDenied
          title="Device Access Restricted"
          description="This task is not available on the current device type."
          details={`Current device: ${effectiveDeviceType}. Allowed: ${(allowedDeviceTypes || []).join(', ')}.`}
          reason="device"
        />
      );
    }

    emitDenied('module', `Missing modules: ${(accessResult.missingModules || []).join(', ')}`);
    return (
      <PermissionDenied
        title="Module Not Enabled"
        description="This area is currently disabled for your tenant configuration."
        details={`Required modules: ${(requiredModules || []).join(', ')}.`}
        reason="module"
      />
    );
  }

  const quickStartGuardResult = evaluateQuickStartModuleGuards({
    environmentStage: tenant.environmentStage,
    requiredModules,
    quickStart: tenant.quickStart,
  });
  const canOverrideGuard = user.role === Role.Admin || user.role === Role.Manager;
  if (!quickStartGuardResult.allowed && !canOverrideGuard) {
    emitDenied('module_guard', quickStartGuardResult.reason || 'Quick start guard blocked access.');
    return (
      <PermissionDenied
        title="Operational Guard Active"
        description="This area is currently blocked by tenant operational guard policy."
        details={
          quickStartGuardResult.reason === 'missing_operational_checklist'
            ? `Pending checklist: ${(quickStartGuardResult.missingChecklist || []).join(', ')}`
            : quickStartGuardResult.details
        }
        reason="module_guard"
      />
    );
  }

  // 8. Check tenant permissions matrix runtime policy
  if (requiredPermission) {
    if (!permissionResolved) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      );
    }

    if (!permissionAllowed) {
      if (permissionError) {
        emitDenied('permission_service', 'Tenant permission matrix could not be verified.');
        return (
          <PermissionDenied
            title="Permission Service Unavailable"
            description="We could not verify your access right now. Please try again shortly."
            details="Access checks are fail-closed for safety. If this persists, contact your administrator."
            reason="service"
          />
        );
      }

      emitDenied('permission', `Permission denied for ${requiredPermission.module}.${requiredPermission.action}.`);
      return (
        <PermissionDenied
          title="Permission Required"
          description="You do not currently have permission to perform this action."
          details={`Module: ${requiredPermission.module}. Action: ${requiredPermission.action}.`}
          reason="permission"
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
